<template>
	<div class="ci-upload-classified-docs">
		<header class="ucd-head">
			<p class="ucd-eyebrow">Importaci&oacute;n</p>
			<h2 class="ucd-heading">Subir documentos clasificados</h2>
			<p class="ucd-sub">
				Importa el archivo con todos los documentos de la empresa ya
				clasificados (formato del export &laquo;Data clasificada&raquo;).
			</p>
		</header>

		<section class="ucd-flow-body">
			<!-- Empresa con mayor o acumulada: el import no aplica (D12). -->
			<div v-if="blockedReason" class="import-errors">
				<div class="import-errors-head">Esta empresa no admite el archivo</div>
				<p class="import-errors-hint">{{ blockedReason }}</p>
			</div>

			<div v-else class="field-group">
				<div class="field-eyebrow">
					Archivo de documentos clasificados
					<span class="field-eyebrow-note">.xlsx</span>
				</div>
				<p class="field-hint">
					Los documentos que ya existen en la empresa (por ejemplo, los que
					trajo Fintoc) <strong>se conservan</strong> y reciben la
					clasificaci&oacute;n del archivo; los que no existen se crean.
					Nada se borra. Antes de guardar ver&aacute;s un resumen por mes para
					confirmar.
				</p>
				<details class="ucd-guide">
					<summary class="ucd-guide-summary">
						C&oacute;mo preparar el archivo — revisa antes de subir
					</summary>
					<ul class="ucd-guide-list">
						<li>
							<strong>Formato:</strong> las mismas columnas del export
							&laquo;Data clasificada&raquo; (Rut, Contraparte, fechas, N&uacute;mero de
							documento, tipo, montos abiertos, Porcentaje, Anula o modifica,
							Level 1/2/3 y las dos categor&iacute;as). Una columna
							&laquo;Origen&raquo; adicional se ignora.
						</li>
						<li>
							<strong>Tipo:</strong> el nombre oficial del documento (Factura
							afecta, Nota de cr&eacute;dito, Boleta de Honorarios, Recibo,
							Remuneraci&oacute;n…) y el lado Ingreso/Egreso. Los documentos
							de Ventas/Costos Devengados no entran por aqu&iacute;.
						</li>
						<li>
							<strong>Montos:</strong> con la convenci&oacute;n del export: en
							facturas el Monto Base es el neto, en boletas y recibos el bruto.
							Si el Monto Base no cuadra con los montos abiertos, la fila se
							rechaza.
						</li>
						<li>
							<strong>Varias filas por documento:</strong> un documento repartido
							en cuentas o devengado en varios meses ocupa una fila por cuenta
							y mes, con el mismo n&uacute;mero de documento; los porcentajes del
							documento deben sumar 100.
						</li>
						<li>
							<strong>Cuentas y categor&iacute;as:</strong> deben existir en el
							plan de la empresa. Las cuentas faltantes se listan en el resumen
							y puedes crearlas desde ah&iacute;.
						</li>
						<li>
							<strong>Clasificaci&oacute;n manual previa:</strong> se conserva,
							salvo que marques &laquo;sobrescribir tambi&eacute;n las
							clasificaciones manuales&raquo; antes de confirmar.
						</li>
					</ul>
				</details>
				<UploadFile
					:key="uploadFileKey"
					v-show="!uploading"
					fileName="archivo de documentos clasificados"
					messageFormat="XLSX"
					extensionAccept=".xlsx"
					:isLoading="uploading"
					@uploaded="onUploaded"
					@removed-file="onRemovedFile"
				/>

				<div v-if="uploading" class="import-readout">
					<div class="import-readout-head">
						<div class="import-readout-text">
							<span class="import-readout-title">{{ readoutTitle }}</span>
							<span v-if="file" class="import-readout-file">{{ file.name }}</span>
						</div>
						<span v-if="displayProgress !== null" class="import-readout-pct">
							{{ displayProgress }}<span class="import-readout-pct-unit">%</span>
						</span>
					</div>
					<div class="track" :class="{ 'is-indeterminate': displayProgress === null }">
						<div
							class="track-fill"
							:style="displayProgress === null ? null : { width: displayProgress + '%' }"
						></div>
					</div>
				</div>
			</div>

			<!-- Cuentas y categorías faltantes: bloquean hasta crearlas (D4). -->
			<div v-if="missingAccounts.length || missingCategories.length" class="import-errors">
				<div class="import-errors-head">
					Faltan cuentas o categor&iacute;as en la empresa
				</div>
				<p class="import-errors-hint">
					El archivo usa cuentas o categor&iacute;as que la empresa no tiene.
					Crea las cuentas desde aqu&iacute; (las categor&iacute;as, en la pesta&ntilde;a
					Categor&iacute;as) y vuelve a validar.
				</p>
				<ul class="import-errors-list">
					<li v-for="(item, i) in missingAccounts" :key="'a' + i" class="import-errors-item">
						<span class="import-errors-row">Cuenta</span>
						<span class="import-errors-reason">
							{{ item.level_3 }} ({{ item.level_1 }} / {{ item.level_2 }}) &middot;
							{{ item.documents }} {{ item.documents === 1 ? "documento" : "documentos" }}
						</span>
					</li>
					<li v-for="(item, i) in missingCategories" :key="'c' + i" class="import-errors-item">
						<span class="import-errors-row">Categor&iacute;a</span>
						<span class="import-errors-reason">
							{{ item.type }} / {{ item.name }} &middot;
							{{ item.documents }} {{ item.documents === 1 ? "documento" : "documentos" }}
						</span>
					</li>
				</ul>
				<div class="ucd-missing-actions">
					<button
						v-if="missingAccounts.length"
						type="button"
						class="ap-btn ap-btn--ghost"
						:disabled="creatingAccounts || !pendingRunId"
						@click="createMissingAccounts()"
					>
						{{ creatingAccounts ? "Creando…" : `Crear ${missingAccounts.length === 1 ? "la cuenta faltante" : `las ${missingAccounts.length} cuentas faltantes`} en el plan` }}
					</button>
				</div>
			</div>

			<!-- Preview (estado C): qué haría el archivo por mes. -->
			<div v-if="pendingPreview.length" class="import-confirm">
				<div class="import-confirm-head">Qu&eacute; har&aacute; el archivo, por mes</div>
				<p class="import-confirm-hint">
					<strong>Nuevos</strong> se crean; <strong>reclasificados</strong> ya
					exist&iacute;an y reciben la clasificaci&oacute;n del archivo;
					<strong>conservados</strong> tienen clasificaci&oacute;n manual y no se
					tocan; <strong>sin clasificar</strong> quedan para el Clasificador.
				</p>
				<ul class="import-confirm-list">
					<li v-for="item in previewRows" :key="item.month" class="import-confirm-item">
						<span class="import-confirm-month">{{ item.month }}</span>
						<span class="import-confirm-detail">
							{{ item.new_docs }} nuevos &middot; {{ item.reclassified }} reclasificados
							&middot; {{ item.preserved }} conservados &middot; {{ item.unclassified }} sin clasificar
						</span>
					</li>
				</ul>
				<p v-if="siiFloor" class="import-confirm-hint">
					Al confirmar, Fintoc dejar&aacute; de traer documentos anteriores al
					<strong>{{ formatDate(siiFloor) }}</strong> (la fecha m&aacute;s reciente del archivo).
				</p>
				<label v-if="preservedTotal" class="ucd-overwrite">
					<input v-model="overwriteManual" type="checkbox" />
					Sobrescribir tambi&eacute;n las {{ preservedTotal }} clasificaciones manuales
				</label>
				<!-- La casilla viaja al confirmar; el resumen la refleja sin volver
					al servidor (decisión 2026-10-16): los conservados pasan a
					reclasificados en las filas de arriba. -->
				<p v-if="overwriteManual" class="import-confirm-hint ucd-overwrite-note">
					Con la casilla marcada, {{ preservedTotal === 1 ? "el documento conservado se reclasifica" : `los ${preservedTotal} documentos conservados se reclasifican` }} al confirmar.
				</p>
			</div>

			<div v-if="warningsList.length" class="import-warnings">
				<div class="import-warnings-head">
					{{ needsConfirmation ? "Revisa antes de importar" : "Advertencias del archivo" }}
				</div>
				<ul class="import-warnings-list">
					<li v-for="(warning, i) in warningsList" :key="i" class="import-warnings-item">
						<span class="import-warnings-title">{{ warning.title }}</span>
						<p v-if="warning.detail" class="import-warnings-detail">{{ warning.detail }}</p>
						<p v-if="warning.examples && warning.examples.length" class="import-warnings-examples">
							{{ warning.examples.join(", ") }}
						</p>
					</li>
				</ul>
			</div>

			<div v-if="invalidRows.length" class="import-errors">
				<div class="import-errors-head">
					No se import&oacute; nada &middot; {{ invalidRows.length }}
					{{ invalidRows.length === 1 ? "fila con problema" : "filas con problemas" }}
				</div>
				<p class="import-errors-hint">Corrige estas filas en el Excel y vuelve a subirlo.</p>
				<ul class="import-errors-list">
					<li v-for="(err, i) in invalidRows" :key="i" class="import-errors-item">
						<span class="import-errors-row">Fila {{ err.row }}</span>
						<span class="import-errors-reason">{{ err.error }}</span>
					</li>
				</ul>
			</div>

			<div v-if="importError" class="import-errors">
				<div class="import-errors-head">No se import&oacute; el archivo</div>
				<p class="import-errors-hint">{{ importError }}</p>
			</div>
		</section>
		<footer v-if="!blockedReason" class="ucd-flow-foot">
			<b-button
				class="is-primary"
				aria-label="Subir documentos clasificados"
				:disabled="!canSubmit"
				:loading="uploading"
				@click="submitFile(needsConfirmation && canConfirm)"
			>
				{{ submitLabel }}
			</b-button>
		</footer>

		<aside class="ucd-side">
			<div
				v-if="lastRun && lastRun.status === 'E' && !uploading && !importError && !invalidRows.length"
				class="ucd-last-error"
			>
				<span class="ucd-last-error-title">El &uacute;ltimo intento fall&oacute;</span>
				<p v-if="lastRun.error" class="ucd-last-error-text">{{ lastRun.error }}</p>
				<p class="ucd-last-error-meta">
					{{ formatDateTime(lastRun.uploaded_at) }}<template v-if="lastRun.created_by"> &middot; {{ lastRun.created_by }}</template>
				</p>
			</div>

			<ImportRecordCard
				v-if="lastSuccess"
				title="Última importación"
				:at="lastSuccess.finished_at || ''"
				:by="lastSuccess.created_by || ''"
				:rows="lastSuccessRows"
			/>
			<ImportRecordCard
				v-if="lastDeletion"
				title="Última eliminación"
				:at="lastDeletion.finished_at || ''"
				:by="lastDeletion.created_by || ''"
				:rows="lastDeletionRows"
			/>

			<!-- Cobertura: sólo los documentos CREADOS por el archivo; eliminar
				por meses saca esos y nunca los que trajo Fintoc. -->
			<div v-if="monthsLoaded.length" class="field-group">
				<div class="field-eyebrow">Meses con documentos del archivo</div>
				<MonthCoverageGrid :monthsLoaded="monthsLoaded" selectable :selected.sync="selectedMonths" />
				<p class="ucd-delete-hint">
					Toca un mes para elegirlo. Sin selecci&oacute;n, se eliminan todos los
					documentos creados por el archivo. Los documentos que ya exist&iacute;an
					(Fintoc) no se tocan.
				</p>
				<div class="ucd-delete-actions">
					<button
						v-if="selectedMonths.length"
						type="button"
						class="ap-btn ap-btn--ghost"
						:disabled="deleting"
						@click="selectedMonths = []"
					>
						Quitar selecci&oacute;n
					</button>
					<button
						type="button"
						class="ap-btn ap-btn--danger"
						:disabled="!canDelete"
						:aria-label="deleteLabel"
						@click="openDeleteConfirm()"
					>
						{{ deleting ? "Eliminando…" : deleteLabel }}
					</button>
				</div>
			</div>

			<div v-if="lastRun && !hasSideContext" class="ucd-empty">
				<span class="ucd-empty-title">Sin importaciones todav&iacute;a</span>
				<p class="ucd-empty-text">
					Esta empresa no tiene documentos cargados por archivo. Sube el export
					de documentos clasificados para crearlos y clasificarlos.
				</p>
			</div>
		</aside>
	</div>
</template>

<script>
import ImportRecordCard from "@/components/companyInfo/ImportRecordCard";
import MonthCoverageGrid from "@/components/companyInfo/MonthCoverageGrid";
import ConfirmRestore from "@/components/modals/ConfirmRestore";
import UploadFile from "@/components/uploadFile/UploadFile";
import {
	continueClassifiedDocsRun,
	createClassifiedDocsMissingAccounts,
	deleteClassifiedDocsDocuments,
	getClassifiedDocsImportStatus,
	uploadClassifiedDocsImport,
} from "@/api/carteraAnalista";
import { formatMonthLabel } from "@/utils/dateLabels";
import { consoleHandlerError, notificate } from "@/utils/message";
import {
	POLL_MAX_CONSECUTIVE_FAILURES,
	isPollOverdue,
	nextPollDelay,
} from "@/utils/importPolling";
import moment from "moment";

// Import del archivo de documentos clasificados
// (docs/decisiones/41-import-documentos-clasificados.md). Mismo esqueleto que CompanyInfoUploadSalesCosts: POST
// 202 y sondeo de import_status (P/S/E/C). Diferencias: la corrida SIEMPRE
// pasa por 'C' (preview por mes) antes de escribir; 'C' con cuentas
// faltantes no se puede confirmar hasta crearlas (atajo) y volver a validar;
// la casilla «sobrescribir manuales» viaja al confirmar.
export default {
	name: "CompanyInfoUploadClassifiedDocs",
	components: { ImportRecordCard, MonthCoverageGrid, UploadFile },
	props: {
		idCompany: { type: [Number, String], required: true, default: null },
	},
	data() {
		return {
			file: null,
			uploading: false,
			lastRun: null,
			pollTimer: null,
			pollStartedAt: null,
			pollFailures: 0,
			pollOverdueNotified: false,
			progress: null,
			uploadPercent: null,
			uploadPhase: "",
			invalidRows: [],
			needsConfirmation: false,
			canConfirm: false,
			pendingRunId: null,
			pendingFileRetained: false,
			pendingPreview: [],
			missingAccounts: [],
			missingCategories: [],
			siiFloor: null,
			overwriteManual: false,
			creatingAccounts: false,
			warningsList: [],
			importError: "",
			uploadFileKey: 0,
			selectedMonths: [],
			deleting: false,
			// Borrado en curso en el worker (202): se sondea `last_deletion`.
			deletionRunId: null,
			deletionTimer: null,
			deletionPollStartedAt: null,
		};
	},
	created() {
		this.loadLastRun();
	},
	beforeDestroy() {
		this.clearPollTimer();
		this.clearDeletionTimer();
	},
	computed: {
		blockedReason() {
			return (this.lastRun && this.lastRun.blocked_reason) || "";
		},
		// Confirmar o revalidar por referencia no necesita archivo en la sección.
		canSubmit() {
			if (this.uploading || this.blockedReason) return false;
			if (this.needsConfirmation && this.pendingFileRetained) return true;
			return !!this.file;
		},
		displayProgress() {
			return this.uploadPhase === "uploading" ? this.uploadPercent : this.progress;
		},
		readoutTitle() {
			if (this.uploadPhase === "uploading") return "Subiendo el archivo…";
			return this.needsConfirmation ? "Importando documentos…" : "Validando el archivo…";
		},
		submitLabel() {
			if (this.uploading) return "Procesando…";
			if (!this.needsConfirmation) return "Validar archivo";
			return this.canConfirm ? "Confirmar e importar" : "Volver a validar";
		},
		preservedTotal() {
			return this.pendingPreview.reduce((total, item) => total + (item.preserved || 0), 0);
		},
		// Filas del preview tal como quedarán al confirmar: con la casilla
		// marcada, los conservados cuentan como reclasificados.
		previewRows() {
			if (!this.overwriteManual) return this.pendingPreview;
			return this.pendingPreview.map((item) => ({
				...item,
				reclassified: (item.reclassified || 0) + (item.preserved || 0),
				preserved: 0,
			}));
		},
		monthsLoaded() {
			return (this.lastRun && this.lastRun.months_loaded) || [];
		},
		lastSuccess() {
			return (this.lastRun && this.lastRun.last_success) || null;
		},
		lastDeletion() {
			return (this.lastRun && this.lastRun.last_deletion) || null;
		},
		lastSuccessRows() {
			const last = this.lastSuccess || {};
			return [
				{ label: "Documentos nuevos", value: last.documents_created },
				{ label: "Reclasificados", value: last.documents_classified },
				{ label: "Filas leídas", value: last.rows_parsed },
				{ label: "Meses", value: this.monthsLabel(last.months), text: true },
			];
		},
		lastDeletionRows() {
			const last = this.lastDeletion || {};
			return [
				{ label: "Documentos eliminados", value: last.documents_deleted },
				{ label: "Meses", value: this.monthsLabel(last.months), text: true },
			];
		},
		hasSideContext() {
			return !!this.lastSuccess || !!this.lastDeletion || this.monthsLoaded.length > 0;
		},
		documentsToDelete() {
			return this.monthsLoaded
				.filter((item) => !this.selectedMonths.length || this.selectedMonths.includes(item.month))
				.reduce((total, item) => total + (item.documents || 0), 0);
		},
		deleteLabel() {
			const count = this.selectedMonths.length;
			if (!count) return "Eliminar todo lo creado por archivo";
			return count === 1 ? "Eliminar 1 mes" : `Eliminar ${count} meses`;
		},
		canDelete() {
			return !this.uploading && !this.deleting && this.monthsLoaded.length > 0;
		},
	},
	methods: {
		monthsLabel(months) {
			// Muchos meses (toda la historia): sólo el rango.
			const list = months || [];
			if (list.length > 6) {
				return `${formatMonthLabel(list[0])} a ${formatMonthLabel(list[list.length - 1])} (${list.length} meses)`;
			}
			return list.map(formatMonthLabel).join(", ");
		},
		formatDate(value) {
			return value ? moment(value).format("DD/MM/YYYY") : "";
		},
		formatDateTime(value) {
			return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
		},
		loadLastRun() {
			return getClassifiedDocsImportStatus(this.idCompany)
				.then(({ data }) => {
					this.lastRun = data;
					if (data.status === "P") {
						this.uploading = true;
						if (typeof data.progress === "number") this.progress = data.progress;
						this.startPolling();
					} else if (data.status === "C" && data.file_retained) {
						// Una corrida en C con archivo retenido (recarga de la página)
						// sigue confirmable por referencia.
						this.applyPendingRun(data);
					}
					const deletion = data.last_deletion;
					if (deletion && deletion.status === "P" && !this.deleting) {
						this.deleting = true;
						this.deletionRunId = deletion.run_id;
						this.deletionPollStartedAt = Date.now();
						this.scheduleDeletionPoll();
					}
				})
				.catch((error) => {
					consoleHandlerError({
						error,
						customDefaultMessage: "No se pudo leer la última importación de documentos clasificados",
						empty: true,
					});
				});
		},
		onUploaded(file) {
			this.file = file;
			this.resetConfirmation();
		},
		onRemovedFile() {
			this.file = null;
			this.resetConfirmation();
		},
		resetConfirmation() {
			this.needsConfirmation = false;
			this.canConfirm = false;
			this.pendingRunId = null;
			this.pendingFileRetained = false;
			this.pendingPreview = [];
			this.missingAccounts = [];
			this.missingCategories = [];
			this.siiFloor = null;
		},
		applyPendingRun(data) {
			const result = data.result || {};
			this.needsConfirmation = true;
			this.canConfirm = !!result.can_confirm;
			this.pendingRunId = data.run_id === undefined ? null : data.run_id;
			this.pendingFileRetained = !!data.file_retained;
			this.pendingPreview = data.replacement_preview || [];
			this.missingAccounts = result.missing_accounts || [];
			this.missingCategories = result.missing_categories || [];
			this.siiFloor = result.sii_documents_floor || null;
			this.warningsList = data.warnings || [];
			this.invalidRows = data.invalid_rows && data.invalid_rows.length && !this.missingAccounts.length
				? data.invalid_rows
				: [];
		},
		// `confirm` = la corrida en C ya se revisó y se escribe. Sin él, una
		// subida nueva (o una revalidación por referencia) sólo valida.
		submitFile(confirm = false) {
			if (!this.canSubmit) return;
			this.uploading = true;
			this.progress = null;
			this.uploadPercent = null;
			this.invalidRows = [];
			this.warningsList = [];
			this.importError = "";
			const flags = { confirm, overwriteManual: confirm && this.overwriteManual };
			const byReference = this.pendingRunId !== null && this.pendingFileRetained;
			let submit;
			if (byReference) {
				submit = continueClassifiedDocsRun(this.idCompany, this.pendingRunId, flags);
			} else {
				this.uploadPhase = "uploading";
				submit = uploadClassifiedDocsImport(this.idCompany, this.file, flags, {
					onUploadProgress: this.onUploadProgress,
				});
			}
			submit
				.then(() => {
					this.uploadPhase = "";
					this.startPolling({ immediate: true });
				})
				.catch((error) => {
					this.uploading = false;
					this.uploadPhase = "";
					const data = error.response && error.response.data;
					const httpStatus = error.response && error.response.status;
					if (httpStatus === 409) {
						notificate((data && data.error) || "Ya hay una importación en curso para esta empresa.", "is-warning is-light");
					} else if (byReference && httpStatus === 400) {
						this.pendingFileRetained = false;
						notificate(
							"La corrida ya no está disponible. Vuelve a subir el archivo.",
							"is-warning is-light"
						);
					} else if (!error.response) {
						this.resumeIfImportStarted();
					} else {
						consoleHandlerError({
							error,
							customDefaultMessage: (data && data.error) || "No se pudo subir el archivo",
							empty: true,
						});
					}
				});
		},
		onUploadProgress(event) {
			if (event && event.total) {
				this.uploadPercent = Math.min(100, Math.round((event.loaded / event.total) * 100));
			}
		},
		resumeIfImportStarted() {
			getClassifiedDocsImportStatus(this.idCompany)
				.then(({ data }) => {
					if (data.status === "P") {
						this.uploading = true;
						notificate("La conexión se cortó, pero la importación quedó en curso.", "is-info is-light");
						this.startPolling({ immediate: true });
						return;
					}
					this.reportInterruptedUpload();
				})
				.catch(() => this.reportInterruptedUpload());
		},
		reportInterruptedUpload() {
			this.importError =
				"La subida del archivo se interrumpió (conexión o tiempo de espera). Vuelve a intentarlo.";
			notificate(`<strong>No se pudo subir el archivo</strong><br>${this.importError}`, "is-warning is-light");
		},
		createMissingAccounts() {
			if (this.creatingAccounts || !this.pendingRunId) return;
			this.creatingAccounts = true;
			return createClassifiedDocsMissingAccounts(this.idCompany, this.pendingRunId)
				.then(({ data }) => {
					const created = (data.created || []).length + (data.linked || []).length;
					notificate(
						`<strong>${created} ${created === 1 ? "cuenta creada" : "cuentas creadas"} en el plan</strong><br>Vuelve a validar el archivo.`,
						"is-success is-light"
					);
					this.missingAccounts = data.rejected || [];
					this.$emit("accounts-created", data);
				})
				.catch((error) => {
					const data = error.response && error.response.data;
					consoleHandlerError({
						error,
						customDefaultMessage: (data && (data.error || data.detail)) || "No se pudieron crear las cuentas",
						empty: true,
					});
				})
				.finally(() => {
					this.creatingAccounts = false;
				});
		},
		startPolling({ immediate = false } = {}) {
			this.clearPollTimer();
			this.pollStartedAt = Date.now();
			this.pollFailures = 0;
			this.pollOverdueNotified = false;
			if (immediate) return this.pollImportStatus();
			this.schedulePoll();
			return Promise.resolve();
		},
		pollElapsedMs() {
			return this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
		},
		schedulePoll() {
			this.clearPollTimer();
			this.pollTimer = setTimeout(this.pollImportStatus, nextPollDelay(this.pollElapsedMs()));
		},
		pollImportStatus() {
			this.pollTimer = null;
			return getClassifiedDocsImportStatus(this.idCompany)
				.then(({ data }) => {
					this.pollFailures = 0;
					if (data.status === "S") {
						this.onImportFinished(data, true);
					} else if (data.status === "E") {
						this.onImportFinished(data, false);
					} else if (data.status === "C") {
						this.uploading = false;
						this.clearPollTimer();
						this.lastRun = data;
						this.applyPendingRun(data);
					} else {
						if (typeof data.progress === "number") this.progress = data.progress;
						if (!this.pollOverdueNotified && isPollOverdue(this.pollElapsedMs())) {
							this.pollOverdueNotified = true;
							notificate(
								"La importación está tardando más de lo habitual. Seguimos revisando su estado y la sección se actualizará al terminar.",
								"is-warning is-light"
							);
						}
						this.schedulePoll();
					}
				})
				.catch((error) => {
					this.pollFailures += 1;
					if (this.pollFailures === POLL_MAX_CONSECUTIVE_FAILURES) {
						consoleHandlerError({
							error,
							customDefaultMessage: "No se pudo leer el estado de la importación. Seguimos intentando.",
							empty: true,
						});
					}
					this.schedulePoll();
				});
		},
		onImportFinished(data, success) {
			this.progress = success ? 100 : this.progress;
			this.uploading = false;
			this.clearPollTimer();
			this.file = null;
			this.uploadFileKey += 1;
			this.uploadPhase = "";
			this.resetConfirmation();
			this.overwriteManual = false;
			this.lastRun = data;
			if (success) {
				const result = data.result || {};
				notificate(
					`<strong>Documentos importados</strong><br>${data.documents_created} nuevos y ${result.documents_classified || 0} reclasificados de ${data.rows_parsed} filas.`,
					"is-success is-light"
				);
				this.warningsList = data.warnings || [];
				this.$emit("imported", data);
			} else if (data.invalid_rows && data.invalid_rows.length) {
				this.invalidRows = data.invalid_rows;
			} else {
				this.importError = data.error || "";
				notificate(`<strong>No se pudo importar el archivo</strong><br>${data.error || ""}`, "is-danger is-light");
			}
		},
		openDeleteConfirm() {
			if (!this.canDelete) return;
			const months = this.selectedMonths;
			const docs = this.documentsToDelete;
			const docsText = docs === 1 ? "1 documento" : `${docs} documentos`;
			const scope = months.length
				? `de ${months.length === 1 ? "el mes" : "los meses"} <strong>${months.join(", ")}</strong>`
				: "de <strong>todos los meses</strong>";
			this.$buefy.modal.open({
				parent: this,
				width: "25%",
				hasModalCard: true,
				canCancel: ["escape", "outside"],
				customClass: "center-always-modal",
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: "confirmDelete",
					tittleRestore: months.length
						? "¿Eliminar los documentos del archivo de los meses elegidos?"
						: "¿Eliminar todos los documentos creados por el archivo?",
					subTittleRestore:
						`Se eliminarán ${docsText} ${scope}, con su clasificación. ` +
						"Los documentos que ya existían (Fintoc) no se tocan. " +
						"Para recuperarlos, vuelve a subir el archivo.",
					msgOkAction: "Eliminar",
				},
				events: { confirmDelete: () => this.deleteDocuments(months) },
			});
		},
		// El backend encola el borrado (202) y la corrida se sondea en
		// `import_status.last_deletion` hasta que cierre en S/E.
		deleteDocuments(months = []) {
			if (this.deleting) return;
			this.deleting = true;
			return deleteClassifiedDocsDocuments(this.idCompany, months)
				.then(({ data }) => {
					this.deletionRunId = data.run_id;
					this.deletionPollStartedAt = Date.now();
					return this.pollDeletionStatus();
				})
				.catch((error) => {
					this.deleting = false;
					const data = error.response && error.response.data;
					const httpStatus = error.response && error.response.status;
					if (httpStatus === 409) {
						notificate(
							"Hay una importación o una eliminación en curso para esta empresa. Espera a que termine antes de eliminar.",
							"is-warning is-light"
						);
					} else {
						consoleHandlerError({
							error,
							customDefaultMessage: (data && data.error) || "No se pudieron eliminar los documentos",
							empty: true,
						});
					}
				});
		},
		scheduleDeletionPoll() {
			this.clearDeletionTimer();
			this.deletionTimer = setTimeout(
				this.pollDeletionStatus,
				nextPollDelay(Date.now() - (this.deletionPollStartedAt || Date.now()))
			);
		},
		pollDeletionStatus() {
			this.deletionTimer = null;
			return getClassifiedDocsImportStatus(this.idCompany)
				.then(({ data }) => {
					const deletion = data.last_deletion;
					const finished =
						deletion && deletion.run_id === this.deletionRunId && deletion.status !== "P";
					if (!finished) {
						this.scheduleDeletionPoll();
						return;
					}
					this.onDeletionFinished(data, deletion);
				})
				.catch(() => this.scheduleDeletionPoll());
		},
		onDeletionFinished(data, deletion) {
			this.clearDeletionTimer();
			this.deleting = false;
			this.deletionRunId = null;
			this.lastRun = data;
			if (deletion.status === "S") {
				const docs = deletion.documents_deleted;
				const monthsCount = (deletion.months || []).length;
				notificate(
					`<strong>Documentos eliminados</strong><br>${docs} ${docs === 1 ? "documento" : "documentos"} de ${monthsCount} ${monthsCount === 1 ? "mes" : "meses"}.`,
					"is-success is-light"
				);
				this.selectedMonths = [];
				this.$emit("deleted", deletion);
			} else {
				notificate(
					`<strong>No se pudieron eliminar los documentos</strong><br>${deletion.error || ""}`,
					"is-danger is-light"
				);
			}
		},
		clearDeletionTimer() {
			if (this.deletionTimer) {
				clearTimeout(this.deletionTimer);
				this.deletionTimer = null;
			}
		},
		clearPollTimer() {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer);
				this.pollTimer = null;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss) — mismo
// lenguaje visual que CompanyInfoUploadSalesCosts: paper → mist, acento teal de
// estado. Sección de la ficha (renderiza dentro de .ci-panel de CompanyInfo).

// Grid de dos columnas: header a lo ancho; flujo (body fila 2 + footer fila 3)
// a la izquierda, contexto a la derecha. Sin wrappers extra: cada hijo se
// ubica por área. Bajo 900px colapsa a una columna (flujo primero).
.ci-upload-classified-docs {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	column-gap: 24px;
}

.ucd-head {
	grid-column: 1 / -1;
}

.ucd-flow-body {
	grid-column: 1;
	grid-row: 2;
}

.ucd-flow-foot {
	grid-column: 1;
	grid-row: 3;
}

.ucd-side {
	grid-column: 2;
	grid-row: 2 / span 2;
	align-self: start;
	position: sticky;
	top: 24px;
}

@media (max-width: 900px) {
	.ci-upload-classified-docs {
		grid-template-columns: minmax(0, 1fr);
	}

	.ucd-flow-body,
	.ucd-flow-foot,
	.ucd-side {
		grid-column: 1;
		grid-row: auto;
	}

	.ucd-side {
		position: static;
		margin-top: 16px;
	}
}

// -- Aviso de último intento fallido (columna de contexto) --------------------
.ucd-last-error {
	margin-bottom: 12px;
	padding: 12px 14px;
	background-color: var(--ap-alert-wash);
	border: 1px solid rgba(192, 69, 59, 0.28);
	border-radius: var(--ap-radius-sm);
}

.ucd-last-error-title {
	display: block;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--ap-alert);
}

.ucd-last-error-text {
	margin: 4px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: var(--ap-ink-2);
}

.ucd-last-error-meta {
	margin: 6px 0 0;
	font-size: 11.5px;
	color: var(--ap-muted);
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

// -- Estado vacío de la columna de contexto ----------------------------------
.ucd-delete-hint {
	margin: 8px 0 0;
	font-size: 11.5px;
	line-height: 1.45;
	color: var(--ap-muted);
}

.ucd-delete-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 10px;
}

// Botones del panel (mismo lenguaje que CompanyInfo_Categories).
.ap-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border: 1px solid transparent;
	border-radius: var(--ap-radius-pill);
	padding: 6px 14px;
	height: auto;
	font-family: var(--ap-font);
	font-size: 12.5px;
	font-weight: 600;
	line-height: 1.2;
	cursor: pointer;
	transition: background-color 0.18s var(--ap-ease),
		color 0.18s var(--ap-ease),
		border-color 0.18s var(--ap-ease),
		transform 0.18s var(--ap-ease);

	&:hover:not([disabled]) {
		transform: translateY(-1px);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	&[disabled] {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.ap-btn--ghost {
	background: var(--ap-paper);
	border-color: var(--ap-line-strong);
	color: var(--ap-ink-2);

	&:hover:not([disabled]) {
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
	}
}

.ap-btn--danger {
	background: var(--ap-alert-wash);
	color: var(--ap-alert);

	&:hover:not([disabled]) {
		background: var(--ap-alert);
		color: var(--ap-paper);
	}
}

.ucd-empty {
	padding: 14px;
	background-color: var(--ap-mist);
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
}

.ucd-empty-title {
	display: block;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--ap-ink);
}

.ucd-empty-text {
	margin: 4px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: var(--ap-slate);
}

.ucd-head {
	margin-bottom: 16px;
}

.ucd-eyebrow {
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.ucd-heading {
	margin: 0;
	font-size: 17px;
	font-weight: 700;
	letter-spacing: -0.015em;
	color: var(--ap-ink);
}

.ucd-sub {
	margin: 4px 0 0;
	font-size: 12.5px;
	line-height: 1.4;
	color: var(--ap-slate);
}

.ucd-flow-body {
	background-color: var(--ap-paper);
}

.field-group {
	margin-bottom: 12px;
}

.field-group:last-child {
	margin-bottom: 0;
}

.field-eyebrow {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.field-eyebrow::before {
	content: "";
	flex: none;
	width: 2px;
	height: 10px;
	border-radius: 1px;
	background-color: var(--ap-teal);
}

.field-eyebrow-note {
	padding: 1px 8px;
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: none;
	color: var(--ap-slate);
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-pill);
}

.field-hint {
	margin-bottom: 10px;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-slate);

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

// -- Guía de preparación del archivo (plegable, cerrada por defecto) ---------

.ucd-guide {
	margin-bottom: 12px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.ucd-guide-summary {
	padding: 8px 12px;
	font-size: 12px;
	font-weight: 600;
	color: var(--ap-ink-2);
	cursor: pointer;
	user-select: none;

	&::marker {
		color: var(--ap-teal);
	}

	&:hover {
		color: var(--ap-ink);
	}
}

.ucd-guide-list {
	margin: 0;
	padding: 2px 12px 10px 28px;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-slate);

	li + li {
		margin-top: 6px;
	}

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

.ucd-guide-types {
	display: block;
	margin-top: 3px;
	font-size: 11.5px;
	color: var(--ap-muted);
}

// -- Dropzone (UploadFile compartido) — mismos overrides que UploadLedger ---
.ucd-flow-body :deep(.uploadFile),
.ucd-flow-body :deep(.upload),
.ucd-flow-body :deep(.upload .upload-draggable){
	display: block;
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
	height: auto;
}

.ucd-flow-body :deep(.upload .upload-draggable){
	padding: 0;
	border: 1.5px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	background-color: var(--ap-mist);
	transition: border-color 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease);
}

.ucd-flow-body :deep(.upload .upload-draggable:hover),
.ucd-flow-body :deep(.upload .upload-draggable.is-hovered){
	border-color: var(--ap-teal);
	background-color: var(--ap-teal-wash);
}

.ucd-flow-body :deep(.upload .section){
	width: 100%;
	height: auto;
	padding: 8px 16px;
	box-sizing: border-box;
}

.ucd-flow-body :deep(.upload .content),
.ucd-flow-body :deep(.upload .content p){
	max-width: 100%;
	white-space: normal;
	overflow-wrap: anywhere;
}

.ucd-flow-body :deep(.upload .content p:not(.has-text-grey-light)){
	font-size: 13px;
	font-weight: 500;
	color: var(--ap-ink-2);
}

.ucd-flow-body :deep(.upload .content .has-text-grey-light){
	font-size: 12px;
	color: var(--ap-muted) !important;
}

.ucd-flow-body :deep(.upload .icon),
.ucd-flow-body :deep(.upload .icon.has-text-primary){
	color: var(--ap-teal) !important;
}

.ucd-flow-body :deep(.upload .icon.is-large){
	height: 1.6rem;
	width: 1.6rem;
	margin: 0 auto;
}

.ucd-flow-body :deep(.upload .icon.is-large .mdi::before){
	font-size: 1.4rem;
}

.ucd-flow-body :deep(.upload .content){
	line-height: 1.3;
}

.ucd-flow-body :deep(.upload .content p:not(:last-child)){
	margin-bottom: 2px;
}

.ucd-flow-body :deep(.uploadFile .tags){
	margin-top: 8px;
	margin-bottom: 0;
}

.ucd-flow-body :deep(.uploadFile .tag.is-primary){
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	height: auto;
	margin-bottom: 0;
	padding: 6px 8px 6px 10px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-left: 3px solid var(--ap-teal);
	border-radius: var(--ap-radius-sm);
	color: var(--ap-ink-2);
	font-size: 12.5px;
	font-weight: 500;
	white-space: normal;
	overflow-wrap: anywhere;
}

.ucd-flow-body :deep(.uploadFile .tag.is-primary .delete){
	flex: none;
	margin-left: 10px;
	background-color: var(--ap-slate);
}

.ucd-flow-body :deep(.uploadFile .tag.is-primary .delete:hover){
	background-color: var(--ap-alert);
}

// -- Readout del import -----------------------------------------------------
.import-readout {
	padding: 16px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.import-readout-head {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.import-readout-text {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.import-readout-title {
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-ink);
}

.import-readout-file {
	font-size: 12px;
	color: var(--ap-slate);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.import-readout-pct {
	flex: none;
	font-size: 18px;
	font-weight: 700;
	color: var(--ap-teal-deep);
	font-variant-numeric: tabular-nums;
	letter-spacing: -0.01em;
}

.import-readout-pct-unit {
	margin-left: 1px;
	font-size: 11px;
	font-weight: 600;
	color: var(--ap-slate);
}

.track {
	position: relative;
	height: 8px;
	border-radius: var(--ap-radius-pill);
	background-color: var(--ap-line);
	overflow: hidden;
}

.track-fill {
	height: 100%;
	border-radius: inherit;
	background: linear-gradient(90deg, var(--ap-teal), var(--ap-teal-deep));
	transition: width 0.4s var(--ap-ease);
}

.track.is-indeterminate .track-fill {
	display: none;
}

.track.is-indeterminate::after {
	content: "";
	position: absolute;
	top: 0;
	bottom: 0;
	width: 38%;
	border-radius: inherit;
	background: linear-gradient(
		90deg,
		transparent,
		var(--ap-teal),
		transparent
	);
	animation: track-slide 1.4s var(--ap-ease) infinite;
}

@keyframes track-slide {
	0% {
		transform: translateX(-120%);
	}
	100% {
		transform: translateX(320%);
	}
}

// -- Confirmación de reemplazo / advertencias ---------------------------------
.import-confirm {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line-strong);
	border-left: 3px solid var(--ap-teal-deep);
	border-radius: var(--ap-radius-sm);
}

.import-confirm-head {
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-ink);
}

.import-confirm-hint {
	margin-top: 2px;
	margin-bottom: 10px;
	font-size: 12px;
	color: var(--ap-slate);

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

.import-confirm-list {
	margin: 0;
	padding: 0;
	list-style: none;
	border-top: 1px solid var(--ap-line);
}

.import-confirm-item {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 4px 10px;
	padding: 8px 0;
	border-bottom: 1px solid var(--ap-line);
	font-size: 12.5px;
}

.import-confirm-month {
	flex: none;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-confirm-detail {
	flex: 1;
	min-width: 160px;
	color: var(--ap-ink-2);
	font-variant-numeric: tabular-nums;
}

.import-warnings {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.import-warnings-head {
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--ap-slate);
}

.import-warnings-list {
	margin: 6px 0 0;
	padding: 0;
	list-style: none;
}

.import-warnings-item {
	padding: 8px 0;
	font-size: 12.5px;
	color: var(--ap-ink-2);
}

.import-warnings-item + .import-warnings-item {
	border-top: 1px solid var(--ap-line);
}

.import-warnings-title {
	display: block;
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-warnings-detail {
	margin: 2px 0 0;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-ink-2);
}

.import-warnings-examples {
	margin: 6px 0 0;
	font-size: 11.5px;
	line-height: 1.5;
	color: var(--ap-muted);
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

// -- Filas inválidas / error global -----------------------------------------
.import-errors {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-alert-wash);
	border: 1px solid var(--ap-alert);
	border-radius: var(--ap-radius-sm);
}

.import-errors-head {
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-alert);
}

.import-errors-hint {
	margin-top: 2px;
	margin-bottom: 10px;
	font-size: 12px;
	color: var(--ap-slate);
}

.import-errors-list {
	max-height: 180px;
	overflow-y: auto;
	margin: 0;
	padding: 0;
	list-style: none;
	border-top: 1px solid var(--ap-line);
}

.import-errors-item {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 4px 10px;
	padding: 8px 0;
	border-bottom: 1px solid var(--ap-line);
	font-size: 12.5px;
}

.import-errors-row {
	flex: none;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-errors-reason {
	flex: 1;
	min-width: 160px;
	color: var(--ap-ink-2);
}

// -- Footer + botones ---------------------------------------------------------
.ucd-flow-foot {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid var(--ap-line);
}

.ci-upload-classified-docs :deep(.button){
	font-family: var(--ap-font);
	font-weight: 600;
	font-size: 13px;
	height: auto;
	padding: 7px 14px;
	border-radius: var(--ap-radius-sm);
	transition: transform 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease),
		border-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease);

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	&[disabled] {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

.ucd-flow-foot .button {
	padding: 8px 18px;
}

.ci-upload-classified-docs :deep(.button.is-primary){
	background-color: var(--ap-ink);
	border-color: var(--ap-ink);
	color: var(--ap-paper);

	&:hover:not([disabled]) {
		background-color: var(--ap-teal-deep);
		border-color: var(--ap-teal-deep);
		transform: translateY(-1px);
	}

	&:active:not([disabled]) {
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.track.is-indeterminate::after {
		animation: none;
	}

	.track-fill {
		transition: none;
	}
}

// -- Propios de este import ----------------------------------------------------
.ucd-missing-actions {
	margin-top: 10px;
	display: flex;
	gap: 8px;
}

.ucd-overwrite {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 10px;
	font-size: 13px;
	color: var(--ap-ink-2);
	cursor: pointer;
}
</style>
